/**
 * The weeks she is quietly pleased and will not say so.
 *
 * `proud` has been computed since moods shipped — a per-account, per-week coin
 * in `mood.ts` — and until now nothing in the app read it. It was a mood she
 * could be in with no way to be in it.
 *
 * The register is the hard part. A narrator whose whole contract is deadpan
 * cannot suddenly be encouraging; „šauniai padirbėta" would be a different
 * character wearing her name. So pride here arrives sideways: she takes the
 * credit herself, she treats a good week as suspicious, she complains that
 * consistency is leaving her with nothing to do. Warmth is the thing under the
 * sentence, never the sentence.
 */

import { fmtKcal, overBy, voc } from '../fmt.js'
import type { Line } from './index.js'

export const PROUD_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'log_success.base': [
    () => 'Įrašyta. Šią savaitę tu kažką darai teisingai. Nesakysiu ką — dar sugadinsi.',
    () => 'Užrašiau. Pradedu įtarti, kad tau tai net patinka.',
    () => 'Gera savaitė. Statistiškai tai reiškia, kad artėja bloga. Kol kas gera.',
    () => 'Tokiu tempu man netrukus nebeliks ką komentuoti. Nemalonu.',
    () => 'Šaldytuvas sako, kad tu pasikeitei. Aš sakiau, kad palauksim ir pažiūrėsim.',
    () => 'Priimta. Neįprastai tvarkinga savaitė. Kažkas čia negerai.',
    () => 'Jei taip toliau, teks susirasti kitą hobį. Turiu porą amžių patirties, susirasiu.',
    () => 'Šios savaitės tavo versija man patinka labiau. Nesakyk jai, dar išpuiks.',
    () => 'Užrašiau. Svarstyklės tavęs nebebijo. Retas dalykas šituose namuose.',
    () => 'Gerai. Nieko juokingo neturiu, o tai iš manęs jau komplimentas.',
  ],
  'greeting.morning': [
    (c) => `Rytas${voc(c)}. Savaitė laikosi. Nesustok vien dėl to, kad pastebėjau.`,
    () => 'Rytas. Ši savaitė tau sekasi, o man tai kelia įtarimą.',
    () => 'Atsikėlei. Savaitė gera — turiu omeny skaičius, dėl viso kito nesikišu.',
  ],
  'greeting.day': [
    () => 'Diena įpusėjo, savaitė laikosi. Retas derinys šituose namuose.',
    () => 'Pusiaudienis. Kol kas viskas taip, kaip turėtų būti. Neįprasta.',
  ],
  // A good week, and somebody asked after her. She takes the credit.
  'chip_comeback.otu': [
    () => 'Puikiai. Savaitė gera, o gerą savaitę visada kas nors organizuoja. Neminėsiu kas.',
    () => 'Neblogai. Turiu mažiau ką komentuoti, ir tai mane šiek tiek erzina.',
    () => 'Gerai. Tiek gerai, kad net pradedu nerimauti.',
  ],
  'chip_comeback.daryt': [
    () => 'Nieko nekeisk. Šitą sakinį iš manęs išgirsi retai.',
    () => 'Tęsk tą patį. Nuobodu, veikia, ir aš neturiu prie ko prikibti.',
  ],
  'greeting.evening': [
    () => 'Vakaras. Gera savaitė beveik atlaikyta. Beveik.',
    () => 'Vakaras. Jei šįvakar nieko nesugadinsi, savaitė bus tavo.',
  ],
  day_under: [
    (c) =>
      `Liko ${fmtKcal(c.remainingKcal ?? 0)} kcal ir gera savaitė. Suvalgyk ką nors normalaus.`,
    () => 'Diena eina teisinga linkme. Nesakau to dažnai, tai pasižymėk.',
  ],
  'day_over.small': [
    (c) => `Viršyta ${overBy(c)} kcal. Po tokios savaitės tai net ne įvykis.`,
    (c) => `${overBy(c)} kcal virš. Savaitė vis tiek geresnė už mane pirmadienį.`,
  ],
  'day_over.big': [
    (c) => `Viršyta ${overBy(c)} kcal. Gera savaitė, bloga diena. Taip irgi būna.`,
    (c) => `${overBy(c)} kcal. Vienos dienos savaitė neatlaiko, bet ši atlaikys.`,
  ],
  'empty_today.evening': [
    () => 'Gera savaitė ir tuščia diena. Nesugadink to dabar, kai jau beveik.',
    () => 'Tuščia. Savaitė laikėsi be manęs, bet vieno įrašo vis dėlto prašau.',
  ],
}
